import { httpClient, toClientApiError } from "./httpClient";

export type TenantUser = {
  membershipId: string;
  membershipStatus: string;
  userId: string;
  email: string;
  displayName: string;
  userStatus: string;
  roles: Array<{ key: string; name: string }>;
  createdAt: string;
  updatedAt: string;
};

export type RoleOption = {
  key: string;
  name: string;
};

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export const usersApi = {
  list(tenantId?: string): Promise<{ items: TenantUser[]; roleOptions: RoleOption[] }> {
    return wrap(async () => {
      const res = await httpClient.get<{
        items: TenantUser[];
        roleOptions: RoleOption[];
      }>("/users", {
        params: tenantId ? { tenantId } : undefined,
      });
      return res.data;
    });
  },

  create(input: {
    email: string;
    displayName: string;
    password?: string;
    invite?: boolean;
    roleKey: string;
    tenantId?: string;
  }): Promise<{ item: TenantUser; temporaryPassword?: string }> {
    return wrap(async () => {
      const res = await httpClient.post<{
        item: TenantUser;
        temporaryPassword?: string;
      }>("/users", input);
      return res.data;
    });
  },

  disable(membershipId: string, tenantId?: string): Promise<TenantUser> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: TenantUser }>(
        `/users/${membershipId}/disable`,
        {},
        { params: tenantId ? { tenantId } : undefined },
      );
      return res.data.item;
    });
  },

  enable(membershipId: string, tenantId?: string): Promise<TenantUser> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: TenantUser }>(
        `/users/${membershipId}/enable`,
        {},
        { params: tenantId ? { tenantId } : undefined },
      );
      return res.data.item;
    });
  },

  assignRole(
    membershipId: string,
    roleKey: string,
    tenantId?: string,
  ): Promise<TenantUser> {
    return wrap(async () => {
      const res = await httpClient.post<{ item: TenantUser }>(
        `/users/${membershipId}/role`,
        { roleKey },
        { params: tenantId ? { tenantId } : undefined },
      );
      return res.data.item;
    });
  },
};
